import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import type { components } from './schema'

export type Tag = components['schemas']['Tag']
export type CreateTagRequest = components['schemas']['CreateTagRequest']
export type UpdateTagRequest = components['schemas']['UpdateTagRequest']
export type TagAssignRequest = components['schemas']['TagAssignRequest']

// Query key helpers
export const tagKeys = {
    all: (orgCode: string) => ['tags', orgCode] as const,
    list: (orgCode: string, query?: Record<string, string | undefined>) => ['tags', orgCode, 'list', query] as const,
    detail: (orgCode: string, tagUid: string) => ['tags', orgCode, tagUid] as const,
    entity: (orgCode: string, entityType: string, entityUid: string) => ['tags', orgCode, 'entity', entityType, entityUid] as const,
}

// Shared with callers that need a one-off, cache-aware fetch outside a
// component (e.g. resolving a tag's code lazily) via queryClient.fetchQuery
// with the same tagKeys.list(orgCode) key.
export async function fetchTagsList(orgCode: string, query?: { q?: string; target_entity_type?: string }): Promise<Tag[]> {
    const response = await apiClient.GET('/api/v1/organizations/{orgCode}/tags', {
        params: {
            path: { orgCode },
            query: {
                q: query?.q,
                target_entity_type: query?.target_entity_type as 'asset' | 'document' | 'user' | 'contract' | 'issue' | 'expense' | 'conversation' | undefined,
                limit: 200,
            },
        },
    })
    if (!response.data) throw new Error('Failed to load tags')
    return (response.data as { data?: Tag[] })?.data || []
}

export function useTags(orgCode: string, query?: { q?: string; target_entity_type?: string }) {
    return useQuery({
        queryKey: tagKeys.list(orgCode, query as Record<string, string | undefined>),
        queryFn: () => fetchTagsList(orgCode, query),
    })
}

export function useTagsWithCounts(orgCode: string, query?: { q?: string; target_entity_type?: string }) {
    return useQuery({
        queryKey: [...tagKeys.all(orgCode), 'list-with-counts', query] as const,
        queryFn: async (): Promise<Tag[]> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/tags', {
                params: {
                    path: { orgCode },
                    query: {
                        q: query?.q,
                        target_entity_type: query?.target_entity_type as 'asset' | 'document' | 'user' | 'contract' | 'issue' | 'expense' | 'conversation' | undefined,
                        include: 'counts',
                        limit: 200,
                    } as Record<string, string | number>,
                },
            })
            if (!response.data) throw new Error('Failed to load tags')
            return (response.data as { data?: Tag[] })?.data || []
        },
    })
}

export function useTagEntities(
    orgCode: string,
    tagUid: string,
    query?: { entity_type?: string; q?: string; page?: number; limit?: number },
) {
    return useQuery({
        queryKey: ['tag-entities', orgCode, tagUid, query] as const,
        queryFn: async () => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/tags/{tagUid}/entities', {
                params: {
                    path: { orgCode, tagUid },
                    query: query as Record<string, string | number> | undefined,
                },
            })
            if (!response.data) throw new Error('Failed to load tag entities')
            return response.data
        },
        enabled: !!tagUid,
    })
}

export function useTag(orgCode: string, tagUid: string) {
    return useQuery({
        queryKey: tagKeys.detail(orgCode, tagUid),
        queryFn: async (): Promise<Tag> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/tags/{tagUid}', {
                params: { path: { orgCode, tagUid } },
            })
            if (!response.data) throw new Error('Failed to load tag')
            return response.data as Tag
        },
        enabled: !!tagUid,
    })
}

export function useEntityTags(orgCode: string, entityType: string, entityUid: string) {
    return useQuery({
        queryKey: tagKeys.entity(orgCode, entityType, entityUid),
        queryFn: async (): Promise<Tag[]> => {
            const response = await apiClient.GET('/api/v1/organizations/{orgCode}/tags/entity', {
                params: {
                    path: { orgCode },
                    query: {
                        entity_type: entityType as 'asset' | 'document' | 'user' | 'contract' | 'issue' | 'expense' | 'conversation',
                        entity_uid: entityUid,
                    },
                },
            })
            if (!response.data) throw new Error('Failed to load entity tags')
            // The contract is a bare array; anything else is no tag (the section maps over it).
            return Array.isArray(response.data) ? (response.data as Tag[]) : []
        },
        enabled: !!entityUid,
    })
}

export function useCreateTag(orgCode: string) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data: CreateTagRequest) => {
            const response = await apiClient.POST('/api/v1/organizations/{orgCode}/tags', {
                params: { path: { orgCode } },
                body: data,
            })
            if (response.error) throw new Error(JSON.stringify(response.error))
            if (!response.data) throw new Error('Failed to create tag')
            return response.data as Tag
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: tagKeys.all(orgCode) })
        },
    })
}

export function useUpdateTag(orgCode: string, tagUid: string) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data: UpdateTagRequest) => {
            const response = await apiClient.PATCH('/api/v1/organizations/{orgCode}/tags/{tagUid}', {
                params: { path: { orgCode, tagUid } },
                body: data,
            })
            if (response.error) throw new Error(JSON.stringify(response.error))
            if (!response.data) throw new Error('Failed to update tag')
            return response.data as Tag
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: tagKeys.all(orgCode) })
        },
    })
}

export function useDeleteTag(orgCode: string) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (tagUid: string) => {
            await apiClient.DELETE('/api/v1/organizations/{orgCode}/tags/{tagUid}', {
                params: { path: { orgCode, tagUid } },
            })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: tagKeys.all(orgCode) })
        },
    })
}

export function useAssignTag(orgCode: string) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data: { entity_type: string; entity_uid: string; tag_uid: string }) => {
            await apiClient.POST('/api/v1/organizations/{orgCode}/tags/assign', {
                params: { path: { orgCode } },
                body: {
                    entity_type: data.entity_type as 'asset' | 'document' | 'user' | 'contract' | 'issue' | 'expense' | 'conversation',
                    entity_uid: data.entity_uid,
                    tag_uid: data.tag_uid,
                },
            })
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: tagKeys.entity(orgCode, variables.entity_type, variables.entity_uid) })
        },
    })
}

export function useUnassignTag(orgCode: string) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data: { entity_type: string; entity_uid: string; tag_uid: string }) => {
            await apiClient.DELETE('/api/v1/organizations/{orgCode}/tags/assign', {
                params: { path: { orgCode } },
                body: {
                    entity_type: data.entity_type as 'asset' | 'document' | 'user' | 'contract' | 'issue' | 'expense' | 'conversation',
                    entity_uid: data.entity_uid,
                    tag_uid: data.tag_uid,
                },
            })
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: tagKeys.entity(orgCode, variables.entity_type, variables.entity_uid) })
        },
    })
}
